import { useEffect, useRef, useState } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { VoiceButton, appendText } from "@/components/VoiceButton";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RichEditor } from "@/components/rich/RichEditor";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/CategoryBadge";
import { ExtractionPreview } from "@/components/ExtractionPreview";
import { QuotePreview } from "@/components/quotes/QuotePreview";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { allCategories, catLabel } from "@/lib/lore";

const SAMPLE = "Kael, exiled prince of Varn, fled to the Ashen Coast in year 412 after the Siege of Highmoor. He carries the Emberblade, forged by the Order of the Pale Flame, and is hunted by his sister Queen Ysolde.";

export default function NotesPage() {
  const { universe, canEdit } = useOutletContext();
  const [text, setText] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("auto");
  const location = useLocation();
  const navigate = useNavigate();
  const quick = location.state?.quickNote;
  const [topic] = useState(location.state?.noteTopic || null);
  const handled = useRef(null);
  useEffect(() => {
    if (!quick || handled.current === location.key) return;
    handled.current = location.key;
    setText((p) => appendText(p, quick));
    setExtraction(null);
    navigate(location.pathname, { replace: true, state: null });
  }, [quick, location.pathname, location.key, navigate]);

  const process = async () => {
    setBusy(true);
    setExtraction(null);
    try {
      const { data } = await api.post(`/universes/${universe.id}/ai/process-note`, { text, category: category === "auto" ? null : category });
      setExtraction(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10">
      <PageHeader eyebrow="AI scribe" title="Notes" />
      {topic && <div className="mb-6 rounded-md border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-4 py-3 text-sm text-[#E9E3FF]" data-testid="note-topic-banner">Suggested by your chapter review: write a note about <b>{topic}</b>. Nothing has been written for you.</div>}
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div>
          <p className="text-sm text-[#A8A090] mb-4 max-w-lg">Paste a raw note: a character sketch, a battle, a half-remembered legend. The scribe will sort it into your Lore Bible and timeline. You review everything before it's saved.</p>
          <div className="rounded-md border border-[#2D3442] bg-[#13171F] focus-within:border-[#D97706]/60 transition-colors">
            <RichEditor value={text} onChange={setText} disabled={!canEdit} testid="note-input" placeholder={SAMPLE}
              className="font-display text-xl leading-relaxed sm:px-5" minH="min-h-[240px] sm:min-h-[380px]" />
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#2D3442] px-4 py-3">
              <div className="flex items-center gap-3">
                <VoiceButton uid={universe.id} onText={(t) => setText((p) => appendText(p, t))} disabled={!canEdit || busy} />
                <button className="text-xs text-[#A8A090] hover:text-[#F59E0B]" onClick={() => setText(SAMPLE)} data-testid="note-sample-button" disabled={!canEdit}>Use sample note</button>
              </div>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <Select value={category} onValueChange={setCategory} disabled={!canEdit}>
                  <SelectTrigger className="h-10 min-w-0 flex-1 truncate sm:w-56 sm:flex-none" data-testid="note-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect type</SelectItem>
                    <SelectItem value="Quote">Quote / dialogue</SelectItem>
                    {allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={process} disabled={busy || text.trim().length < 3 || !canEdit} data-testid="process-note-button">
                  <Wand2 className="h-4 w-4 mr-1.5" />{busy ? "Reading…" : "Process note"}
                </Button>
              </div>
            </div>
          </div>
          {!canEdit && <p className="mt-3 text-xs text-[#A8A090]">You have view-only access to this universe.</p>}
        </div>
        <div>
          {busy && (
            <div className="rounded-md border border-[#2D3442] bg-[#13171F] p-8" data-testid="extraction-loading">
              <div className="lf-label mb-4">The scribe is reading</div>
              <p className="font-display text-2xl text-[#E8DFD1] lf-caret">Cataloguing names, places and dates</p>
            </div>
          )}
          {extraction?.kind === "quote" && <QuotePreview key={JSON.stringify(extraction).length} data={extraction} uid={universe.id}
            onDone={() => { setExtraction(null); setText(""); }} onCancel={() => setExtraction(null)} />}
          {extraction && extraction.kind !== "quote" && <ExtractionPreview key={JSON.stringify(extraction).length} extraction={extraction} noteText={text} uid={universe.id}
            onDone={() => { setExtraction(null); setText(""); }} onCancel={() => setExtraction(null)} />}
          {!busy && !extraction && (
            <div className="rounded-md border border-dashed border-[#2D3442] p-10 h-full min-h-[300px] flex flex-col justify-center" data-testid="extraction-empty">
              <div className="lf-label mb-3">Preview</div>
              <p className="font-display text-3xl text-[#F3EBDD]">Nothing transcribed yet.</p>
              <p className="mt-2 text-sm text-[#A8A090]">Extracted entries, attributes, events and links will appear here for review.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
